import {
  useCallback,
  useEffect,
  useState,
  useRef,
}                                      from "react";
import Image                           from "./Image";
import { Grid, Icon }                  from "@gupshup-frontend/gupshup-ts-components";

import Content                         from "./Content";
import OrderStatus                     from "./OrderStatus";
import ProductMessage                  from "./ProductMessage";

import {
  DATA_TESTID,
  TEMPLATE_TYPE,
  DEFAULT_MEDIA_IMAGES,
  TEMPLATE_PREVIEW_DATA_TESTID,
}                                      from "./constant";
import styles                          from "./style.module.css";

const Header = (props) => {

  const {
    containerMeta,
    mediaUrl            = "",
    headerType          = "TEXT",
    bypassMediaUrlCheck = false,
    hidePreview         = false,
    // Change #1: validateMediaUrl used to be a module-level import from the old
    // aliased utilities/common module, backed by a zustand store that does not exist
    // in this repo. It is now an optional prop; when the caller doesn't supply one we
    // just treat the url as already valid, same as bypassMediaUrlCheck does below.
    validateMediaUrl    = async (mediaUrl) => mediaUrl,
  } = props;

  const headerRef = useRef(null);

  const [validMediaUrl, setValidMediaUrl]   = useState('');
  const [isVisible, setIsVisible]           = useState(false);
  const [isMediaLoading, setIsMediaLoading] = useState (false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      setIsVisible(entry.isIntersecting);
    },{ threshold: 0.1 });

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => {
      if (headerRef.current) {
        observer.unobserve(headerRef.current);
      }
    };
  }, [headerType]);

  useEffect(() => {
    if (!mediaUrl) {
      setValidMediaUrl('');
      return;
    }
    if (bypassMediaUrlCheck) {
      setValidMediaUrl(mediaUrl);
      return;
    }
    if (TEMPLATE_TYPE.MEDIA.includes(headerType) && isVisible && !hidePreview) {
      setIsMediaLoading (true);
      validateMediaUrl(mediaUrl).then((isValid) => {
        setIsMediaLoading (false);
        setValidMediaUrl(isValid || '');
      });
    }
  },[mediaUrl, isVisible]);

  const renderVideo = useCallback ((dataTestId) => {
    return (
      <video
        controls
        className   = {styles.mediaPanel}
        data-testid = {`${dataTestId}-panel`}
      >
        <source
          src         = {validMediaUrl || mediaUrl}
          type        = "video/mp4"
          data-testid = {dataTestId}
        />
      </video>
    );
  }, [mediaUrl, validMediaUrl]);

  if((headerType === TEMPLATE_TYPE.TEXT || headerType === TEMPLATE_TYPE.ORDER_DETAILS) && containerMeta?.header) {
    return (
      <Content
        isHeaderContent = {true}
        dataTestId      = {TEMPLATE_PREVIEW_DATA_TESTID.HEADER}
        content         = {containerMeta?.sampleHeader ?? containerMeta.header}
      />
    );
  }

  if(headerType === TEMPLATE_TYPE.PRODUCT) {
    return <ProductMessage content = {containerMeta?.sampleHeader ?? containerMeta.header} />;
  }

  if(headerType === TEMPLATE_TYPE.CATALOG) {
    return <ProductMessage isCatalogMessage = {true} /> ;
  }

  if(headerType === TEMPLATE_TYPE.ORDER_STATUS) {
    return <OrderStatus /> ;
  }

  const renderMedia  = () => {
    const dataTestId = `${DATA_TESTID}-${headerType?.toLowerCase()}-dynamic`;
    switch (headerType) {
      case "IMAGE":
        return (
          <Grid className = {`${styles.mediaPanel} ${styles.imagePanel}`} ref = {headerRef}>
            <Image
              src         = {validMediaUrl}
              alt         = 'media-header'
              loader      = {() => mediaUrl}
              width       = {0}
              height      = {0}
              sizes       = "100vw"
              data-testid = {dataTestId}
              style       = {{ width: '100%', height: 'auto' }}
              onError     = {() => setValidMediaUrl(DEFAULT_MEDIA_IMAGES.IMAGE.image_src)}
            />
          </Grid>
        );
      case "GIF":
      case "VIDEO":
        return renderVideo(dataTestId);
      case "DOCUMENT":
        return (
          <iframe
            src         = {mediaUrl}
            title       = "Document Preview"
            data-testid = {dataTestId}
            className   = {styles.mediaPanel}
          >
          </iframe>
        );
      default:
        return <></>;
    }
  };

  return (
    <Grid ref = {headerRef}>
      {(TEMPLATE_TYPE.MEDIA.includes(headerType) && (mediaUrl && validMediaUrl) && !hidePreview) ?
        renderMedia()
        :
        headerType === TEMPLATE_TYPE.LOCATION || (TEMPLATE_TYPE.MEDIA.includes(headerType) && (!mediaUrl || !validMediaUrl || isMediaLoading || hidePreview)) ?
          <Grid className = {styles.mediaPreviewPanel}>
            {isMediaLoading && <Grid className = {styles.skeletonLoader}></Grid>}
            <Icon
              width      = "-webkit-fill-available"
              alt        = "Media Preview Icon"
              customPath = {DEFAULT_MEDIA_IMAGES?.[headerType]?.image_src}
              dataTestId = {`${DATA_TESTID}-${headerType?.toLowerCase()}-static`}
            />
          </Grid>
          :
          <></>
      }
    </Grid>
  );
};

export default Header;
